// src/pages/SettingBrandStyle.js

import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../contexts/AppContext';
import ColorPalette from '../components/settings/ColorPalette';
import FloatingFooter from '../components/utils/FloatingFooter';
import ConfirmModal from '../components/utils/ConfirmModal';
import InformModal from '../components/utils/InformModal';
import { FaDownload } from 'react-icons/fa';
import { Upload, Button, Checkbox } from 'antd';
import ImgCrop from 'antd-img-crop';
import { transparentLogo } from '../utils/transparentLogo';
import { normalizeHex, ensureFullConfigurationWithMapping, buildThemeFromBaseColor } from '../components/themeEditor/utils';

/** ================= 固定色常量（设计系统可替换） =================
 * 中文：primary 模式下 palette 只保留主色 + 4 个固定色；
 * EN: In primary mode, palette keeps only primary + 4 fixed colors.
 */
const WHITE_HEX = '#ffffff';
const BLACK_HEX = '#000000';
const DARK_GREY_HEX = '#374151';   // dark grey
const LIGHT_GREY_HEX = '#e5e7eb';  // light grey


/** ================= 页面组件 ================= */
const SettingBrandStyle = () => {
  const { token, selectedRole, userInfo } = useContext(AppContext);

  const [editedLanding, setEditedLanding] = useState(null);
  const [initialLanding, setInitialLanding] = useState(null);
  const [editedTheme, setEditedTheme] = useState(null);   // { colors, configuration }
  const [initialTheme, setInitialTheme] = useState(null);

  // 只可能是 'primaryColor' 或 'topBannerBackground'
  const [targetField, setTargetField] = useState('configuration.primaryColor');

  const [useTransparentAlgo, setUseTransparentAlgo] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [informOpen, setInformOpen] = useState(false);
  const [informMsg, setInformMsg] = useState('');
  const [informType, setInformType] = useState('inform');

  /** ================= 预加载：按规则初始化 =================
   * 中文：有 theme → 按规则选择编辑字段并初始化；无 theme → 首建使用映射补齐配置与 5 色盘。
   * EN: If theme exists, init from it; otherwise seed a new theme using mapping + 5-color palette.
   */
  useEffect(() => {
    const spotList = userInfo?.roleInfo?.[selectedRole.type] || [];
    const current = spotList.find((r) => r.id === selectedRole.id);

    if (current?.landing) {
      setEditedLanding({ ...current.landing });
      setInitialLanding({ ...current.landing });
    }

    // —— 主题存在：走既有初始化逻辑
    if (current?.theme) {
      const incomingCfg = current.theme.configuration || {};

      // —— 读取原始值
      const primaryRaw = incomingCfg.primaryColor;
      const bannerRaw = incomingCfg.topBannerBackground;

      const primaryExists = Object.prototype.hasOwnProperty.call(incomingCfg, 'primaryColor');
      const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';
      const primaryEmpty = isEmpty(primaryRaw);
      const bannerEmpty = isEmpty(bannerRaw);

      // —— 目标字段选择（优先编辑 primary）
      let modeKey = 'primaryColor';
      if (!primaryExists) {
        modeKey = 'primaryColor';
      } else if (!primaryEmpty) {
        modeKey = 'primaryColor';
      } else if (primaryEmpty && !bannerEmpty) {
        modeKey = 'topBannerBackground';
      } else {
        modeKey = 'primaryColor';
      }
      setTargetField(modeKey);

      // —— 基础色（primary 模式需要）
      const preferredPrimary = normalizeHex(
        (primaryEmpty ? '#2003fc' : primaryRaw) ||
        current.theme.primaryColor ||
        current.theme.color ||
        '#2003fc'
      );
      const preferredSecondary = normalizeHex(incomingCfg.secondaryColor || WHITE_HEX);

      // —— 准备 configuration
      let fullCfg;
      if (modeKey === 'primaryColor') {
        // 按映射补齐（避免保存时出现空值）
        fullCfg = ensureFullConfigurationWithMapping(incomingCfg, preferredPrimary, preferredSecondary);
      } else {
        // banner 模式：不改其它字段，仅规范化已有值
        fullCfg = { ...incomingCfg };
        Object.keys(fullCfg).forEach((k) => {
          if (typeof fullCfg[k] === 'string') fullCfg[k] = normalizeHex(fullCfg[k]);
        });
      }

      // —— 初始化 colors（按你的规则）
      let initColors;
      if (modeKey === 'primaryColor') {
        // 只保留：primary + 黑/白/深灰/浅灰
        const list = [
          normalizeHex(fullCfg.primaryColor),
          BLACK_HEX,
          WHITE_HEX,
          DARK_GREY_HEX,
          LIGHT_GREY_HEX,
        ]
          .map(normalizeHex)
          .filter(Boolean);
        initColors = Array.from(new Set(list));
      } else {
        // 仅替换 banner 颜色：如果 theme.colors 里有旧 banner 就原位替换，否则追加新 banner
        const base = Array.isArray(current.theme.colors)
          ? current.theme.colors.map(normalizeHex).filter(Boolean)
          : [];
        const oldBanner = normalizeHex(incomingCfg.topBannerBackground);
        const newBanner = normalizeHex(fullCfg.topBannerBackground);
        const idx = base.indexOf(oldBanner);
        if (idx >= 0) {
          const next = [...base];
          next[idx] = newBanner;
          initColors = Array.from(new Set(next));
        } else {
          initColors = Array.from(new Set([...base, newBanner].map(normalizeHex).filter(Boolean)));
        }
      }

      setEditedTheme({ colors: initColors, configuration: fullCfg });
      setInitialTheme({ colors: initColors, configuration: fullCfg });

    } else {
      // —— 主题不存在：首次创建 → 使用映射一次性补齐配置 + 5 色盘
      // Tips(EN): First-time creation, use mapping to fill configuration and 5-color palette.
      const seed = buildThemeFromBaseColor('#2003fc'); // 可按业务设定默认主色
      setTargetField('primaryColor');
      setEditedTheme(seed);
      setInitialTheme(seed);
      // 如需初始化 landing 的默认值，可在此处设置 editedLanding
    }
  }, [userInfo, selectedRole]);

  /** ================= 选色回调（按目标字段规则更新） =================
   * 中文：primary 模式按映射补齐，palette 只保留 5 色；banner 模式仅替换那一个颜色；
   * EN: Primary mode fills all fields via mapping with a 5-color palette; banner mode replaces only that color.
   */
  const handleThemeChange = (payload) => {
    const nextColor = typeof payload === 'string'
      ? payload
      : (payload?.primaryColor ?? payload?.color ?? '');

    const hex = normalizeHex(nextColor);
    if (!hex) return;

    setEditedTheme((prev) => {
      const prevCfg = prev?.configuration || {};
      const rawNextCfg = { ...prevCfg, [targetField]: hex, color:"" };

      if (targetField === 'primaryColor') {
        // 1) primary 模式：按映射补齐 configuration（避免空值）
        const nextCfg = ensureFullConfigurationWithMapping(
          rawNextCfg,
          rawNextCfg.primaryColor,
          rawNextCfg.secondaryColor
        );
        // 2) colors 只保留：primary + 黑/白/深灰/浅灰
        const nextColors = Array.from(new Set([
          normalizeHex(nextCfg.primaryColor),
          BLACK_HEX,
          WHITE_HEX,
          DARK_GREY_HEX,
          LIGHT_GREY_HEX,
        ].map(normalizeHex).filter(Boolean)));
        return { ...prev, configuration: nextCfg, colors: nextColors };
      }

      // —— banner 模式：仅替换旧 banner 那一个色，其余不变
      const base = Array.isArray(prev?.colors) ? prev.colors.map(normalizeHex).filter(Boolean) : [];
      // const oldBanner = normalizeHex(prevCfg.topBannerBackground);
      // const idx = base.indexOf(oldBanner);
      let nextColors;
      // if (idx >= 0) {
      //   const arr = [...base];
      //   arr[idx] = hex;
      //   nextColors = Array.from(new Set(arr));
      // } else {
      //   // 如果旧值不在 colors，则追加新颜色（防重复）
        nextColors = Array.from(new Set([...base, hex]));
        console.log('nextColors', nextColors);
      // }
      return { ...prev, configuration: rawNextCfg, colors: nextColors };
    });
  };

  /** ================= 上传 Logo（沿用原逻辑） ================= */
  const handleCustomUpload = async ({ file, onError, onSuccess }) => {
    try {
      const toProcess = useTransparentAlgo ? await transparentLogo(file) : file;

      const img = new Image();
      const url = URL.createObjectURL(toProcess);
      img.src = url;

      img.onload = async () => {
        const { naturalWidth: width, naturalHeight: height } = img;
        URL.revokeObjectURL(url);

        const fd = new FormData();
        fd.append('image', toProcess);
        fd.append('width', width);
        fd.append('height', height);

        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/private/image/upload`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
        );

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.error || 'Upload failed');
        }

        const data = await resp.json();
        setEditedLanding((prev) => ({ ...prev, logoImage: data.fileUrl }));
        onSuccess(null, toProcess);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        throw new Error('Image processing failed');
      };
    } catch (err) {
      setInformMsg(err.message || 'Failed to upload logo');
      setInformType('error');
      setInformOpen(true);
      onError(err);
    }
  };

  /** ================= 保存（按当前目标字段规则构建配置） =================
   * 中文：primary 模式保存前再次映射补齐；banner 模式不批量改动其它字段；
   * EN: Before saving, primary mode fills via mapping; banner mode keeps other fields intact.
   */
  const save = async () => {
    try {
      const cfgToSend =
        targetField === 'primaryColor'
          ? ensureFullConfigurationWithMapping(
              editedTheme?.configuration,
              editedTheme?.configuration?.primaryColor,
              editedTheme?.configuration?.secondaryColor || WHITE_HEX
            )
          : { ...(editedTheme?.configuration || {}) }; // banner 模式：不批量改动其它字段

      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            landing: editedLanding,
            theme: {
              colors: Array.isArray(editedTheme?.colors) ? editedTheme.colors : [],
              configuration: cfgToSend,
            },
          }),
        }
      );

      if (resp.ok) return { ok: true };
      const data = await resp.json().catch(() => ({}));
      return { ok: false, message: data.error || resp.statusText };
    } catch (e) {
      return { ok: false, message: 'Network error' };
    }
  };

  const onConfirmSave = async () => {
    setConfirmOpen(false);
    const { ok, message } = await save();
    if (ok) {
      window.location.reload();
    } else {
      setInformType('error');
      setInformMsg(message);
      setInformOpen(true);
    }
  };

  const hasChanges = useMemo(
    () =>
      JSON.stringify(editedLanding || null) !== JSON.stringify(initialLanding || null) ||
      JSON.stringify(editedTheme || null) !== JSON.stringify(initialTheme || null),
    [editedLanding, initialLanding, editedTheme, initialTheme]
  );

  if (!editedLanding || !editedTheme) return null;

  /** ================= UI ================= */
  const previewColor = editedTheme.configuration?.[targetField] || '#2003fc';

  return (
    <div className="w-[90%] max-w-7xl mx-auto mb-20">
      <div className="mt-4 rounded-[28px] border border-[#e2e7f6] bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Logo 区 */}
          <div className="space-y-4">
            <div className="text-center">
              <img
                src={editedLanding.logoImage}
                alt="Logo"
                className="w-auto h-[25vh] mx-auto"
                style={{ backgroundColor: previewColor }}
              />
            </div>

            <div className="text-center text-sm text-gray-600">
              Editing field: <strong>{targetField}</strong>
            </div>

            <div className="flex items-center justify-center">
              <Checkbox
                checked={useTransparentAlgo}
                onChange={(e) => setUseTransparentAlgo(e.target.checked)}
              >
                Apply transparent logo algorithm
              </Checkbox>
            </div>

            <div className="flex items-center justify-center space-x-4">
              <ImgCrop rotate aspect={25 / 9}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  customRequest={handleCustomUpload}
                >
                  <Button type="primary">Upload Logo</Button>
                </Upload>
              </ImgCrop>

              {editedLanding.logoImage && (
                <a
                  href={editedLanding.logoImage}
                  download
                  className="text-blue-500 hover:text-blue-700"
                  title="Download current logo"
                >
                  <FaDownload size={20} />
                </a>
              )}
            </div>
          </div>

          {/* 颜色面板：用户选色 → 只更新 targetField；primary 模式才批量映射 */}
          <ColorPalette
            OriginalTheme={initialTheme}
            onChange={handleThemeChange}
          />
        </div>
      </div>

      <FloatingFooter hasChanges={hasChanges} onSave={() => setConfirmOpen(true)} />

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
        header="Confirm Save"
        content="Are you sure you want to save the branding changes?"
      />

      <InformModal
        isOpen={informOpen}
        onClose={() => setInformOpen(false)}
        content={informMsg}
        type={informType}
      />
    </div>
  );
};

export default SettingBrandStyle;
